/**
 * 画像・音声バリエーションの提示順(docs/05_asset_pipeline.md §3c、docs/04_data_model.md §3)。
 *
 * 1語につき画像4種(`media.images[]`)・英語音声4話者(`media.audios.en[]`)が用意される。
 * 出題ごとに1つ選ぶが、**完全ランダムは使わない**(同じ絵・同じ声が偶然連続すると
 * 多様に提示する意味が薄れるため)。代わりに**シャッフル循環**:
 *
 *   wordId ごとに候補をシャッフルして順に消費 → 使い切ったら再シャッフル。
 *   再シャッフルの先頭が直前に出したものと同じなら、別の要素と入れ替える(周回の境目の連続も防ぐ)。
 *
 * `images` / `audios` を持たない語(旧データ・未生成)は従来の `image` / `audio` にフォールバックする。
 *
 * 描画・音声・ネットワークには触らない純ロジック。乱数は DI 可能で、テストは決定的に書ける。
 */

import type { ContentWord, Language } from '../services/content';

/** [0,1) を返す乱数。テストでは決定的な列を差し替える。 */
export type RandomFn = () => number;

/** 1回の出題で使うアセット(コンテンツ側の相対パス。URL 化は呼び出し側)。 */
export interface MediaSelection {
  readonly image?: string;
  readonly audio?: string;
}

/** 系列1本ぶんの状態。 */
interface Cycle {
  /** 候補リストが差し替わったら循環をやり直すための目印。 */
  signature: string;
  /** 未消費の残り。空になったら再シャッフルする。 */
  queue: string[];
  /** 直前に返したもの(周回の境目で同じものを続けないため)。 */
  last: string | null;
}

/**
 * キー(= wordId など)ごとに独立したシャッフル循環を持つ選択器。
 * 状態はインスタンス内だけに持つので、寿命 = 1プレイセッションで捨ててよい。
 */
export class ShuffleCycler {
  private readonly cycles = new Map<string, Cycle>();
  private readonly random: RandomFn;

  constructor(random: RandomFn = Math.random) {
    this.random = random;
  }

  /** 次の1つを返す。候補が空なら undefined、1つだけならそれを返し続ける。 */
  next(key: string, variants: readonly string[]): string | undefined {
    if (variants.length === 0) return undefined;
    if (variants.length === 1) return variants[0];

    const signature = variants.join(' ');
    let cycle = this.cycles.get(key);
    if (!cycle || cycle.signature !== signature) {
      cycle = { signature, queue: [], last: null };
      this.cycles.set(key, cycle);
    }
    if (cycle.queue.length === 0) cycle.queue = this.reshuffle(variants, cycle.last);

    const picked = cycle.queue.shift() as string;
    cycle.last = picked;
    return picked;
  }

  /** Fisher-Yates。先頭が直前と同じになったら後ろの1つと入れ替える。 */
  private reshuffle(variants: readonly string[], last: string | null): string[] {
    const shuffled = [...variants];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = this.pickIndex(i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j] as string, shuffled[i] as string];
    }
    if (last !== null && shuffled[0] === last) {
      const j = 1 + this.pickIndex(shuffled.length - 1);
      [shuffled[0], shuffled[j]] = [shuffled[j] as string, shuffled[0] as string];
    }
    return shuffled;
  }

  /** [0, size) の整数。乱数が範囲外・NaN でも壊れないように丸める。 */
  private pickIndex(size: number): number {
    const value = this.random();
    const index = Math.floor((Number.isFinite(value) ? value : 0) * size);
    return Math.min(Math.max(index, 0), size - 1);
  }
}

/** 画像の候補一覧。`images` が無ければ代表画像1枚(それも無ければ空)。 */
export function imageVariants(word: ContentWord): readonly string[] {
  const images = word.media?.images;
  if (images && images.length > 0) return images;
  const image = word.media?.image;
  return image ? [image] : [];
}

/** 音声の候補一覧。`audios[locale]` が無ければ代表音声1本(それも無ければ空)。 */
export function audioVariants(word: ContentWord, language: Language): readonly string[] {
  const locale = language === 'english' ? 'en' : 'ja';
  const audios = word.media?.audios?.[locale];
  if (audios && audios.length > 0) return audios;
  const audio = word.media?.audio?.[locale];
  return audio ? [audio] : [];
}

/** バリエーション選択を使わない場合の代表アセット(従来挙動)。 */
export function representativeMedia(word: ContentWord, language: Language): MediaSelection {
  const image = word.media?.image ?? imageVariants(word)[0];
  const audio =
    (language === 'english' ? word.media?.audio?.en : word.media?.audio?.ja) ??
    audioVariants(word, language)[0];
  return {
    ...(image === undefined ? {} : { image }),
    ...(audio === undefined ? {} : { audio }),
  };
}

/**
 * 出題1回ぶんの画像・音声を選ぶ(画像と音声は独立した循環)。
 *
 * 1プレイセッションにつき1インスタンス。**選択は wordStart 時に1回だけ**行い、
 * 結果を PlayWord に載せる(出題時再生と打ち終わり時再生が同じ話者になる)。
 */
export class WordMediaSelector {
  private readonly images: ShuffleCycler;
  private readonly audios: ShuffleCycler;

  constructor(random: RandomFn = Math.random) {
    this.images = new ShuffleCycler(random);
    this.audios = new ShuffleCycler(random);
  }

  select(word: ContentWord, language: Language): MediaSelection {
    const image = this.images.next(word.id, imageVariants(word));
    const audio = this.audios.next(`${language}:${word.id}`, audioVariants(word, language));
    return {
      ...(image === undefined ? {} : { image }),
      ...(audio === undefined ? {} : { audio }),
    };
  }
}
